import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CalendarOverview from '../CalendarOverview';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '../ui';
import { getAllGroups, getStaffGroups, getTorneosByGrupoIds } from '../../lib/supabase';

const JuegosOverview = ({ embedded = false }) => {
  const location = useLocation();
  const { user, staff } = useAuth();
  const { isAdmin, isHeadCoach } = usePermissions();
  const initialViewBy = location.state?.viewBy || 'torneo';
  const initialGroupId = location.state?.groupId || '';
  const initialTorneoId = location.state?.torneoId || '';

  const [allGroups, setAllGroups] = useState([]);
  const [staffGroupIds, setStaffGroupIds] = useState([]);
  const [torneos, setTorneos] = useState([]);
  const [groupsByTorneo, setGroupsByTorneo] = useState({});
  const [selectedTorneoId, setSelectedTorneoId] = useState(initialTorneoId);
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);
  const [viewBy, setViewBy] = useState(initialViewBy);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      let groupIds = [];
      let staffIds = [];
      try {
        const { data, error } = await getAllGroups();
        if (error) throw error;
        const groups = (data || []).filter(Boolean);
        setAllGroups(groups);
        groupIds = groups.map((group) => group.id);
      } catch (err) {
        if (staff?.id) {
          const { data, error } = await getStaffGroups(staff.id);
          if (error) throw error;
          const groups = (data || []).map((item) => item.grupos).filter(Boolean);
          setAllGroups(groups);
          groupIds = groups.map((group) => group.id);
          staffIds = groupIds;
        }
      }

      if (!staffIds.length && staff?.id) {
        const { data, error } = await getStaffGroups(staff.id);
        if (!error) {
          staffIds = (data || []).map((item) => item.grupos?.id).filter(Boolean);
        }
      }
      setStaffGroupIds(staffIds);

      const { data: rels, error: relError } = await getTorneosByGrupoIds(groupIds);
      if (relError) throw relError;

      const torneosMap = new Map();
      const groupsMap = new Map();
      (rels || []).forEach((rel) => {
        if (rel.torneos) {
          if (!torneosMap.has(rel.torneos.id)) {
            torneosMap.set(rel.torneos.id, rel.torneos);
          }
        }
        if (rel.grupos && rel.torneos) {
          if (!groupsMap.has(rel.torneos.id)) {
            groupsMap.set(rel.torneos.id, new Map());
          }
          groupsMap.get(rel.torneos.id).set(rel.grupos.id, rel.grupos);
        }
      });

      const list = Array.from(torneosMap.values());
      setTorneos(list);
      const groupObj = {};
      groupsMap.forEach((groupMap, torneoId) => {
        groupObj[torneoId] = Array.from(groupMap.values());
      });
      setGroupsByTorneo(groupObj);
      if (!selectedTorneoId && list.length > 0) {
        setSelectedTorneoId(list[0].id);
      }
      if (!selectedGroupId && groupIds.length > 0) {
        setSelectedGroupId(groupIds[0]);
      }
    } catch (error) {
      console.error('Error cargando juegos:', error);
      setAllGroups([]);
      setTorneos([]);
      setGroupsByTorneo({});
    } finally {
      setLoading(false);
    }
  }, [user, staff?.id, selectedTorneoId, selectedGroupId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const gruposTorneo = useMemo(
    () => (selectedTorneoId ? groupsByTorneo[selectedTorneoId] || [] : []),
    [groupsByTorneo, selectedTorneoId]
  );
  const selectedGroup = useMemo(
    () => allGroups.find((group) => group.id === selectedGroupId) || null,
    [allGroups, selectedGroupId]
  );

  const allowedGroupIds = useMemo(() => {
    if (isAdmin || isHeadCoach) return null;
    return staffGroupIds.length ? staffGroupIds : [];
  }, [isAdmin, isHeadCoach, staffGroupIds]);

  const calendarGroups = useMemo(() => {
    if (viewBy === 'grupo') {
      return selectedGroup ? [selectedGroup] : [];
    }
    return gruposTorneo;
  }, [viewBy, selectedGroup, gruposTorneo]);

  const calendarGroupIds = useMemo(() => {
    if (viewBy === 'grupo') {
      return selectedGroup ? [selectedGroup.id] : [];
    }
    return gruposTorneo.map((group) => group.id);
  }, [viewBy, selectedGroup, gruposTorneo]);

  useEffect(() => {
    if (viewBy === 'torneo' && !selectedTorneoId && torneos.length > 0) {
      setSelectedTorneoId(torneos[0].id);
    }
    if (viewBy === 'grupo' && !selectedGroupId && allGroups.length > 0) {
      setSelectedGroupId(allGroups[0].id);
    }
  }, [viewBy, selectedTorneoId, selectedGroupId, torneos, allGroups]);

  const extraFilterCount =
    (viewBy ? 1 : 0) + ((viewBy === 'torneo' ? selectedTorneoId : selectedGroupId) ? 1 : 0);

  const contenido = (
      <div className="space-y-6">
        <CalendarOverview
          embedded
          showQuickActions={false}
          defaultDisplayMode="agenda"
          allowedTypes={['juego']}
          overrideGroups={calendarGroups}
          initialGroupIds={calendarGroupIds}
          allowedGroupIds={allowedGroupIds}
          extraFilterCount={extraFilterCount}
          extraFilters={(
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Vista de juegos</p>
                <p className="text-[11px] text-muted-foreground">Elige si quieres ver los juegos por torneo o por grupo.</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Ver por</label>
                  <Select value={viewBy} onValueChange={setViewBy}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Selecciona la vista" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="torneo">Torneo</SelectItem>
                      <SelectItem value="grupo">Grupo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {viewBy === 'torneo' ? (
                  <div>
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Torneo</label>
                    <Select value={selectedTorneoId} onValueChange={setSelectedTorneoId}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Selecciona un torneo" />
                      </SelectTrigger>
                      <SelectContent>
                        {torneos.map((torneo) => (
                          <SelectItem key={torneo.id} value={torneo.id}>
                            {torneo.nombre || 'Torneo sin nombre'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Grupo</label>
                    <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Selecciona un grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {allGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.nombre_del_grupo || 'Grupo sin nombre'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}
        />

        {loading && (
          <div className="text-sm text-muted-foreground">Cargando torneos...</div>
        )}
      </div>
  );

  if (embedded) return contenido;

  return (
    <FeaturedLayout
      title="Calendario de juegos"
      subtitle="El mes completo con los partidos de tus torneos internos y de la red, cada uno con su etiqueta. Solo para consultar."
      refreshAction={loadData}
    >
      {contenido}
    </FeaturedLayout>
  );
};

export default JuegosOverview;
